<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import HotelCardCarousel from '@components/ui/hotelPage&index/HotelCardCarousel.vue';
import { type FilteredHotelBlockGroup } from '@search/hotel/types';
import HotelPackage from '@itinerary/hotel/HotelPackage';

const { variant = 'blue' } = defineProps<IProps>();
const hotelForm = useHotelForm();
const { isLoadingHotelBlocking, hotelBlocksGrouped } = moduleToRefs(hotelForm);
interface IProps {
    variant?: 'blue' | 'yellow';
    hideTitle?: boolean;
    icon?: IconDefinition;
}

const blocks = computed<FilteredHotelBlockGroup[]>(() =>
    hotelBlocksGrouped.value.map(hotel => ({
        ...hotel,
        hotelBlockDetails:
            hotel.hotelBlockDetails?.map(details => ({
                ...details,
                blocks: details.blocks
                    .sort((a, b) => (a.priority || 1) - (b.priority || 1))
                    .map(
                        block =>
                            new HotelPackage({
                                hotel: block.hotel,
                                cityName:
                                    block.hotel?.englishCityName?.toLowerCase() ||
                                    undefined,
                                totalPrice: block.salePrice || undefined,
                                payable:
                                    (block.salePrice || 0) -
                                    (block.discountAmount || 0),
                            })
                    ),
            })) || [],
    }))
);

const showHotels = computed(
    () => isLoadingHotelBlocking.value || !!blocks.value.length
);
// if this block is really needed in ssr then we should should keep it , other wise its to ig to be loaded on the server
onServerPrefetch(async () => {
    await hotelForm.fetchBlocking();
});

onMounted(() => {
    if (!hotelBlocksGrouped.value.length) {
        hotelForm.fetchBlocking();
    }
});
</script>

<template>
    <aside v-if="showHotels" id="mr-hotel-blocks">
        <div class="root" :class="variant">
            <template v-for="block in blocks" :key="block.cityId || 0">
                <DelayHydration mode="visible">
                    <div class="blocks-container">
                        <mr-typography
                            class="title"
                            type="h2"
                            weight="700"
                            size="x-large"
                            color="var(--cool-gray-900)"
                        >
                            {{ block.title }}
                        </mr-typography>
                        <HotelCardCarousel
                            reserve-event-name="blocking-hotel-click"
                            class="hotel-cursor"
                            :items="
                                block
                                    ? block.hotelBlockDetails.flatMap(
                                          ({ blocks }) => blocks
                                      )
                                    : []
                            "
                            :max-width="1312"
                            have-loading
                            :is-loading="isLoadingHotelBlocking"
                            single-img
                        />
                    </div>
                </DelayHydration>
            </template>
        </div>
    </aside>
</template>
<style lang="scss" scoped>
.title {
    padding: 12px;
}

.root {
    display: flex;
    flex-direction: column;
    .blocks-container {
        width: 100%;
        max-width: 1200px;
        margin: 0 auto;
        .hotel-cursor {
            width: 100%;
        }
    }
}
</style>
